import { serviceUrls } from '@/config/serviceUrls';
import { protectedFetch } from '@/utils/authSession';

const BASE = serviceUrls.learning.endsWith('/')
  ? serviceUrls.learning.slice(0, -1)
  : serviceUrls.learning;

const VOCAB = `${BASE}/v1/learning/vocabulary`;

export type VocabularyCategory =
  | 'animal' | 'food' | 'vehicle' | 'object' | 'place' | 'person' | 'plant'
  | 'emotion' | 'action' | 'abstract' | 'concept';

export interface VocabularyFocusWord {
  id: string;
  lemma: string;
  surface_form: string;
  word: string;
  meaning_en: string;
  meaning_ta: string;
  synonyms: string[];
  antonyms: string[];
  example_sentence: string;
  context_sentence?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category?: VocabularyCategory;
  image_required: boolean;
  has_image: boolean;
  image_url?: string | null;
  has_tts_word?: boolean;
  has_tts_context?: boolean;
  has_tts_example?: boolean;
  tts_word_url?: string | null;
  tts_context_url?: string | null;
  tts_example_url?: string | null;
  status: 'draft' | 'published';
  focus_word_source?: 'lesson_vocabulary' | 'normalized' | 'generated' | 'llm' | null;
}

export interface VocabularyContext {
  id: string;
  tenant_id: string;
  grade: number;
  book_id: string;
  lesson_no: number;
  display_text: string;
  sort_order: number;
  status: 'draft' | 'published';
  has_tts_context?: boolean;
  tts_context_url?: string | null;
  focus_words: VocabularyFocusWord[];
}

export interface VocabularyEntry {
  id: string;
  tenant_id: string;
  grade: number;
  book_id: string;
  lesson_no: number;
  context_id?: string | null;
  display_text?: string | null;
  context_sentence?: string | null;
  surface_form?: string | null;
  word: string;
  meaning_en: string;
  meaning_ta: string;
  synonyms: string[];
  antonyms: string[];
  example_sentence: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category?: VocabularyCategory;
  image_required: boolean;
  has_image: boolean;
  image_url?: string | null;
  image_source?: 'ai_generated' | 'reused' | 'teacher_upload' | null;
  has_tts_word?: boolean;
  has_tts_context?: boolean;
  has_tts_example?: boolean;
  tts_word_url?: string | null;
  tts_context_url?: string | null;
  tts_example_url?: string | null;
  tts_word_source?: 'generated' | 'reused' | null;
  tts_example_source?: 'generated' | 'reused' | null;
  status: 'draft' | 'published';
  focus_word_source?: 'lesson_vocabulary' | 'normalized' | 'generated' | 'llm' | null;
}

export interface VocabularyLesson {
  lesson_no: number;
  title: string;
  book_id: string;
  total_words?: number;
  learned_words?: number;
  completion_pct?: number;
}

export interface VocabularySummary {
  words_learned: number;
  mastered: number;
  need_practice: number;
  coverage_pct: number;
  total_published_words: number;
  revision_words_learned?: number;
  revision_mastered?: number;
  revision_coverage_pct?: number;
  revision_total_published?: number;
  revision_stars_earned?: number;
}

export interface VocabularyGenerateResult {
  job_id: string;
  status: string;
  word_count: number;
  context_count?: number;
  visual_word_count: number;
  text_word_count: number;
  images_generated: number;
  images_reused: number;
  image_generation_failures: number;
  warning?: string | null;
  entries: VocabularyEntry[];
  contexts?: VocabularyContext[];
}

async function parseJson<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    const body = err as { detail?: string | { error_code?: string; message?: string } };
    let message = r.statusText || 'Request failed';
    if (typeof body.detail === 'string') {
      message = body.detail;
    } else if (body.detail && typeof body.detail === 'object') {
      message = body.detail.message || body.detail.error_code || message;
    }
    if (r.status === 403 && message === 'Forbidden') {
      message = 'FEATURE_NOT_ENABLED_FOR_SCHOOL';
    }
    throw new Error(message);
  }
  return r.json() as Promise<T>;
}

export async function fetchVocabularyLessons(grade: number, bookId?: string): Promise<VocabularyLesson[]> {
  const q = new URLSearchParams({ grade: String(grade) });
  if (bookId) q.set('book_id', bookId);
  const r = await protectedFetch(`${VOCAB}/lessons?${q}`);
  const data = await parseJson<{ items: VocabularyLesson[] }>(r);
  return data.items || [];
}

export async function generateVocabulary(params: {
  grade: number;
  book_id: string;
  lesson_no: number;
}): Promise<VocabularyGenerateResult> {
  const r = await protectedFetch(`${VOCAB}/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return parseJson(r);
}

export async function fetchVocabularyEntries(params: {
  grade?: number;
  lesson_no?: number;
  status?: string;
}): Promise<VocabularyEntry[]> {
  const q = new URLSearchParams();
  if (params.grade != null) q.set('grade', String(params.grade));
  if (params.lesson_no != null) q.set('lesson_no', String(params.lesson_no));
  if (params.status) q.set('status', params.status);
  const r = await protectedFetch(`${VOCAB}/entries?${q}`);
  return parseJson(r);
}

export async function fetchVocabularyContexts(params: {
  grade?: number;
  lesson_no?: number;
  status?: string;
}): Promise<VocabularyContext[]> {
  const q = new URLSearchParams();
  if (params.grade != null) q.set('grade', String(params.grade));
  if (params.lesson_no != null) q.set('lesson_no', String(params.lesson_no));
  if (params.status) q.set('status', params.status);
  const r = await protectedFetch(`${VOCAB}/contexts?${q}`);
  return parseJson(r);
}

export async function updateVocabularyContext(
  id: string,
  patch: { display_text?: string }
): Promise<VocabularyContext> {
  const r = await protectedFetch(`${VOCAB}/contexts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return parseJson(r);
}

export async function deleteVocabularyContext(id: string): Promise<void> {
  const r = await protectedFetch(`${VOCAB}/contexts/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error('Delete failed');
}

export async function updateVocabularyEntry(
  id: string,
  patch: Partial<Pick<VocabularyEntry, 'meaning_en' | 'meaning_ta' | 'synonyms' | 'antonyms' | 'example_sentence' | 'difficulty' | 'category'>>
): Promise<VocabularyEntry> {
  const r = await protectedFetch(`${VOCAB}/entries/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return parseJson(r);
}

export async function enrichMissingVocabularyMetadata(params: {
  grade: number;
  lesson_no: number;
  book_id?: string;
}): Promise<{ enriched_count: number; entries: VocabularyEntry[]; warning?: string | null }> {
  const r = await protectedFetch(`${VOCAB}/entries/enrich-missing-metadata`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return parseJson(r);
}

export async function backfillVocabularyFocusWords(params: {
  grade: number;
  lesson_no: number;
  book_id?: string;
}): Promise<{ updated_count: number; entries: VocabularyEntry[] }> {
  const r = await protectedFetch(`${VOCAB}/entries/backfill-focus-words`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return parseJson(r);
}

export async function generateMissingVocabularyImages(params: {
  grade: number;
  lesson_no: number;
}): Promise<{
  status: 'started' | 'noop' | string;
  attempted: number;
  images_generated: number;
  images_reused: number;
  image_generation_failures: number;
}> {
  const r = await protectedFetch(`${VOCAB}/entries/generate-missing-images`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return parseJson(r);
}

export async function publishVocabulary(params: { ids?: string[]; grade?: number; lesson_no?: number }): Promise<number> {
  const r = await protectedFetch(`${VOCAB}/entries/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const data = await parseJson<{ published_count: number }>(r);
  return data.published_count;
}

export async function deleteVocabularyEntry(id: string): Promise<void> {
  const r = await protectedFetch(`${VOCAB}/entries/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error('Delete failed');
}

export async function regenerateVocabularyImage(id: string): Promise<VocabularyEntry> {
  const r = await protectedFetch(`${VOCAB}/entries/${id}/regenerate-image`, { method: 'POST' });
  return parseJson(r);
}

export async function regenerateVocabularyTts(id: string): Promise<VocabularyEntry> {
  const r = await protectedFetch(`${VOCAB}/entries/${id}/regenerate-tts`, { method: 'POST' });
  return parseJson(r);
}

export async function uploadVocabularyImage(id: string, file: File): Promise<VocabularyEntry> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await protectedFetch(`${VOCAB}/entries/${id}/upload-image`, { method: 'POST', body: fd });
  return parseJson(r);
}

export async function removeVocabularyImage(id: string): Promise<VocabularyEntry> {
  const r = await protectedFetch(`${VOCAB}/entries/${id}/image`, { method: 'DELETE' });
  return parseJson(r);
}

export interface VocabularyJourney {
  words_learned: number;
  words_mastered: number;
  attempted_words: number;
  vocabulary_score: number;
  practice_streak: number;
  mastery_percentage: number;
  recently_learned: string[];
  total_assigned_words: number;
  continue_practice?: {
    lesson_no: number;
    vocabulary_entry_id: string;
    vocabulary_text: string;
    mode: 'practicing' | 'new' | 'review';
  } | null;
}

export async function fetchVocabularyJourney(grade?: number): Promise<VocabularyJourney> {
  const q = grade != null ? `?grade=${grade}` : '';
  const r = await protectedFetch(`${VOCAB}/me/journey${q}`);
  return parseJson(r);
}

export async function fetchVocabularyWordImage(params: {
  flashcardId?: string;
  vocabularyEntryId?: string;
}): Promise<Blob> {
  if (params.flashcardId) {
    const r = await protectedFetch(`${VOCAB}/assets/flashcard/${params.flashcardId}/image`);
    if (!r.ok) throw new Error('Image not found');
    return r.blob();
  }
  if (params.vocabularyEntryId) {
    const r = await protectedFetch(`${VOCAB}/assets/entry/${params.vocabularyEntryId}/image`);
    if (!r.ok) throw new Error('Image not found');
    return r.blob();
  }
  throw new Error('No image id');
}

export async function recordVocabularyView(vocabularyEntryId: string): Promise<{ ok: boolean; learning_state?: string }> {
  const r = await protectedFetch(`${VOCAB}/me/view`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vocabulary_entry_id: vocabularyEntryId }),
  });
  return parseJson(r);
}

export async function recordVocabularyActivity(params: {
  vocabulary_text: string;
  vocabulary_entry_id?: string;
  grade?: number;
  activity_type: 'pronunciation' | 'quiz' | 'sentence' | 'view';
  success: boolean;
}): Promise<{ ok: boolean; learning_state?: string; mastered?: boolean }> {
  const r = await protectedFetch(`${VOCAB}/progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return parseJson(r);
}

export async function fetchStudentVocabularySummary(grade?: number): Promise<VocabularySummary> {
  const q = grade != null ? `?grade=${grade}` : '';
  const r = await protectedFetch(`${VOCAB}/me/summary${q}`);
  return parseJson(r);
}

export async function fetchStudentVocabularyLessons(grade?: number): Promise<VocabularyLesson[]> {
  const q = grade != null ? `?grade=${grade}` : '';
  const r = await protectedFetch(`${VOCAB}/me/lessons${q}`);
  return parseJson(r);
}

export async function fetchStudentLessonVocabulary(lessonNo: number, grade?: number): Promise<VocabularyEntry[]> {
  const q = grade != null ? `?grade=${grade}` : '';
  const r = await protectedFetch(`${VOCAB}/me/lessons/${lessonNo}${q}`);
  return parseJson(r);
}

export async function fetchStudentLessonContexts(lessonNo: number, grade?: number): Promise<VocabularyContext[]> {
  const q = grade != null ? `?grade=${grade}` : '';
  const r = await protectedFetch(`${VOCAB}/me/lessons/${lessonNo}/contexts${q}`);
  return parseJson(r);
}

export async function lookupVocabularyWord(word: string, grade?: number): Promise<VocabularyEntry> {
  const q = new URLSearchParams({ word });
  if (grade != null) q.set('grade', String(grade));
  const r = await protectedFetch(`${VOCAB}/lookup?${q}`);
  return parseJson(r);
}

export interface WordOfDayData {
  word: string;
  meaning_en: string;
  meaning_ta: string;
  example_sentence: string;
  has_image: boolean;
  image_url?: string | null;
  flashcard_id?: string | null;
  vocabulary_entry_id?: string | null;
  word_tts_url?: string | null;
  example_tts_url?: string | null;
  source?: string;
  lesson_no?: number | null;
  category?: string | null;
}

export async function fetchWordOfDay(
  grade?: number,
  lessonNo?: number
): Promise<WordOfDayData | null> {
  const q = new URLSearchParams();
  if (grade != null) q.set('grade', String(grade));
  if (lessonNo != null && lessonNo > 0) q.set('lesson_no', String(lessonNo));
  const qs = q.toString();
  const r = await protectedFetch(`${VOCAB}/word-of-the-day${qs ? `?${qs}` : ''}`);
  if (r.status === 404) return null;
  return parseJson<WordOfDayData>(r);
}

export interface WordOfDayPracticeResult {
  recognized_text: string;
  expected_text: string;
  similarity_score: number;
  passed: boolean;
  feedback_level: string;
  feedback_title: string;
  stars_awarded: number;
  stt_error?: string | null;
}

export async function practiceWordOfDay(params: {
  audio: Blob;
  grade?: number;
  flashcardId?: string;
  vocabularyEntryId?: string;
}): Promise<WordOfDayPracticeResult> {
  const form = new FormData();
  form.append('audio', params.audio, 'recording.webm');
  if (params.flashcardId) form.append('flashcard_id', params.flashcardId);
  if (params.vocabularyEntryId) form.append('vocabulary_entry_id', params.vocabularyEntryId);
  if (params.grade != null) form.append('grade', String(params.grade));
  const r = await protectedFetch(`${VOCAB}/word-of-the-day/practice`, { method: 'POST', body: form });
  return parseJson(r);
}

export async function fetchVocabularyFlashcards(params?: { grade?: number; lesson_no?: number }): Promise<VocabularyEntry[]> {
  const q = new URLSearchParams();
  if (params?.grade != null) q.set('grade', String(params.grade));
  if (params?.lesson_no != null) q.set('lesson_no', String(params.lesson_no));
  const r = await protectedFetch(`${VOCAB}/me/flashcards?${q}`);
  return parseJson(r);
}

export async function recordVocabularyProgress(vocabularyEntryId: string, outcome: 'know' | 'practice_again'): Promise<{ mastered: boolean }> {
  const r = await protectedFetch(`${VOCAB}/me/progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vocabulary_entry_id: vocabularyEntryId, outcome }),
  });
  return parseJson(r);
}

export interface VocabularyPronunciationResult {
  recognized_word: string;
  expected_word: string;
  accuracy_score: number;
  phonetic_similarity: number;
  stt_confidence: number;
  feedback_level: 'excellent' | 'good' | 'needs_practice' | 'try_again';
  feedback_title: string;
  retry_guidance_en: string;
  retry_guidance_ta: string;
  passed: boolean;
  pronunciation_mastered: boolean;
  attempt_count: number;
  pronunciation_best_score: number;
  stt_error?: string | null;
}

export async function validateVocabularyPronunciation(
  vocabularyEntryId: string,
  audioBlob: Blob
): Promise<VocabularyPronunciationResult> {
  const fd = new FormData();
  fd.append('vocabulary_entry_id', vocabularyEntryId);
  fd.append('audio', audioBlob, 'pronunciation.webm');
  const r = await protectedFetch(`${VOCAB}/pronunciation/me/validate`, {
    method: 'POST',
    body: fd,
  });
  return parseJson(r);
}

export async function fetchTeacherPronunciationAnalytics(grade: number, lessonNo: number) {
  const q = new URLSearchParams({ grade: String(grade), lesson_no: String(lessonNo) });
  const r = await protectedFetch(`${VOCAB}/pronunciation/teacher/analytics?${q}`);
  return parseJson<{
    pronunciation_attempts: number;
    pronunciation_mastered: number;
    avg_pronunciation_score: number;
    students_practicing: number;
    weak_pronunciation_words: string[];
    strong_pronunciation_words: string[];
  }>(r);
}

export async function recordVocabularyAudioPlay(
  vocabularyEntryId: string,
  audioType: 'word' | 'example' | 'context'
): Promise<{ word_audio_play_count: number; example_audio_play_count: number; context_audio_play_count?: number }> {
  const r = await protectedFetch(`${VOCAB}/me/progress/audio-play`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vocabulary_entry_id: vocabularyEntryId, audio_type: audioType }),
  });
  return parseJson(r);
}

export type SchoolVocabularyInsights = {
  ok?: boolean;
  tenant_id?: string;
  vocabulary_words_published: number;
  revision_flashcards_published: number;
  students_practiced_today: number;
  average_practice_accuracy_pct: number;
};

export async function fetchSchoolVocabularyInsights(): Promise<SchoolVocabularyInsights> {
  const r = await protectedFetch(`${VOCAB}/admin/school-insights`);
  return parseJson<SchoolVocabularyInsights>(r);
}


export async function fetchTeacherVocabularyAnalytics(grade: number, lessonNo?: number) {
  const q = new URLSearchParams({ grade: String(grade) });
  if (lessonNo != null) q.set('lesson_no', String(lessonNo));
  const r = await protectedFetch(`${VOCAB}/teacher/analytics?${q}`);
  return parseJson<Record<string, number | string | string[]>>(r);
}
